/**
 * Outil « Points mobiles » (Boîte à outils, 2nde → 1re).
 *
 * Deux points A et B qu'on déplace sur le quadrillage (au doigt ou aux
 * flèches) : le milieu I et la longueur AB se recalculent, avec les formules
 * remplies par les coordonnées. Le triangle rectangle dessiné sous [AB]
 * montre d'où vient la formule de la distance (Pythagore). Valeurs exactes :
 * moitiés, racines simplifiées. Défis. Calculs : `points.ts`.
 */

import { useRef, useState } from 'react';
import { MathText, cn } from '@jeraisonne/ui';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import { PRIMARY_BUTTON, ToggleChip } from '../../ui/controls';
import { makeFrame, snapPoint, type Pt } from '../../ui/frame';
import { formatCoords, formatNumber } from '../../ui/format';
import {
  DragHandle,
  FIGURE_BG_CLASS,
  HaloText,
  INK,
  PlotGrid,
  RightAngleMark,
} from '../../ui/svg-parts';
import { useDrag } from '../../ui/use-drag';
import { distance2, distanceExact, GOALS, midpoint } from './points';

const SIZE = 320;
const VP = { xMin: -6.5, xMax: 6.5, yMin: -6.5, yMax: 6.5 };
const frame = makeFrame(VP, SIZE);
const SEG = 'var(--color-primary-500)';
const MID = 'var(--color-accent-700)';
const LEG = 'var(--color-success-600)';
type Name = 'A' | 'B';
const NAMES: readonly Name[] = ['A', 'B'];

const par = (n: number) => (n < 0 ? `(${n})` : String(n));
const tx = (s: string) => s.replace(/,/g, '{,}');

export function MovingPointsTool({ onSend, sendCost }: ToolProps) {
  const [pts, setPts] = useState<Record<Name, Pt>>({ A: { x: -2, y: 1 }, B: { x: 4, y: 4 } });
  const [triangle, setTriangle] = useState(true);
  const [goal, setGoal] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  const drag = useDrag<Name>({
    svgRef,
    frame,
    ids: NAMES,
    pointOf: (n) => pts[n],
    onDrag: (n, p) =>
      setPts((old) => {
        const q = snapPoint(p, VP, 1, 0.5);
        const other = old[n === 'A' ? 'B' : 'A'];
        return q.x === other.x && q.y === other.y ? old : { ...old, [n]: q };
      }),
  });

  const { A, B } = pts;
  const I = midpoint(A, B);
  const d = distanceExact(A, B);
  const dx = B.x - A.x;
  const dy = B.y - A.y;
  const d2 = distance2(A, B);
  const px = (p: Pt) => ({ x: frame.tx(p.x), y: frame.ty(p.y) });
  const [pa, pb] = [px(A), px(B)];
  const pc = px({ x: B.x, y: A.y }); // sommet de l'angle droit
  const pi = px({ x: I.x.value, y: I.y.value });
  const done = goal !== null && GOALS[goal]!.check(A, B);
  // côté de l'étiquette du côté vertical : vers l'extérieur, sauf au bord droit de la figure
  const side = dx > 0 ? (pc.x > SIZE - 28 ? -1 : 1) : pc.x < 28 ? 1 : -1;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <ToggleChip on={triangle} onClick={() => setTriangle((t) => !t)}>
          Triangle rectangle
        </ToggleChip>
        <ToggleChip on={goal !== null} onClick={() => setGoal((g) => (g === null ? 0 : null))}>
          Défi
        </ToggleChip>
      </div>

      {goal !== null && (
        <section
          className="bg-xp-50 space-y-2 rounded-2xl p-3"
          aria-live="polite"
          data-testid="mp-goal"
        >
          <p className="text-ink-900 text-sm font-semibold">
            Défi {goal + 1}/{GOALS.length} · {GOALS[goal]!.text}
          </p>
          {done ? (
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-success-600 text-sm font-bold">Réussi !</p>
              {goal + 1 < GOALS.length && (
                <button
                  type="button"
                  className={cn(PRIMARY_BUTTON, 'ml-auto')}
                  onClick={() => setGoal(goal + 1)}
                >
                  Défi suivant
                </button>
              )}
            </div>
          ) : (
            <p className="text-ink-600 text-sm">Déplace A ou B.</p>
          )}
        </section>
      )}

      <p className="text-ink-700 text-sm" data-testid="mp-points">
        A{formatCoords(A.x, A.y)} · B{formatCoords(B.x, B.y)}
      </p>

      <div className="mx-auto w-full max-w-[26rem]">
        <svg
          ref={svgRef}
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          className={cn(
            'border-ink-100 block aspect-square w-full touch-none select-none rounded-2xl border',
            FIGURE_BG_CLASS,
          )}
          role="group"
          aria-label={`Segment [AB] et son milieu I(${I.x.plain} ; ${I.y.plain}).`}
          {...drag.svgProps}
        >
          <PlotGrid frame={frame} step={1} />
          {triangle && dx !== 0 && dy !== 0 && (
            <g>
              <line
                x1={pa.x}
                y1={pa.y}
                x2={pc.x}
                y2={pc.y}
                stroke={LEG}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              <line
                x1={pc.x}
                y1={pc.y}
                x2={pb.x}
                y2={pb.y}
                stroke={LEG}
                strokeWidth={2}
                strokeDasharray="6 4"
              />
              <RightAngleMark
                x={pc.x}
                y={pc.y}
                ux={pa.x - pc.x}
                uy={0}
                vx={0}
                vy={pb.y - pc.y}
                color={LEG}
              />
              <HaloText
                x={(pa.x + pc.x) / 2}
                y={Math.min(SIZE - 4, Math.max(12, pc.y + (dy > 0 ? 16 : -8)))}
                textAnchor="middle"
                fontSize={13}
                fontWeight={700}
                fill={LEG}
              >
                {String(Math.abs(dx))}
              </HaloText>
              <HaloText
                x={pc.x + (side > 0 ? 8 : -8)}
                y={(pc.y + pb.y) / 2 + 4}
                textAnchor={side > 0 ? 'start' : 'end'}
                fontSize={13}
                fontWeight={700}
                fill={LEG}
              >
                {String(Math.abs(dy))}
              </HaloText>
            </g>
          )}
          <line x1={pa.x} y1={pa.y} x2={pb.x} y2={pb.y} stroke={SEG} strokeWidth={3} />
          <circle cx={pi.x} cy={pi.y} r={6} fill={MID} />
          <HaloText x={pi.x + 9} y={pi.y - 9} fontSize={15} fontWeight={700} fill={MID}>
            I
          </HaloText>
          {NAMES.map((n) => {
            const p = px(pts[n]);
            return (
              <DragHandle
                key={n}
                x={p.x}
                y={p.y}
                color={INK}
                label={n}
                labelDx={p.x < 24 ? 14 : -14}
                labelDy={p.y < 24 ? 16 : -14}
                name={`Point ${n} ${formatCoords(pts[n].x, pts[n].y)}`}
                active={drag.active === n}
                keyProps={drag.keyProps(n)}
              />
            );
          })}
        </svg>
      </div>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="mp-mid"
      >
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">Milieu de [AB]</p>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$x_I = \\frac{x_A + x_B}{2} = \\frac{${A.x} + ${par(B.x)}}{2} = ${I.x.tex}$`}
        </MathText>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$y_I = \\frac{y_A + y_B}{2} = \\frac{${A.y} + ${par(B.y)}}{2} = ${I.y.tex}$`}
        </MathText>
        <p className="text-ink-900 font-bold" aria-live="polite">
          I({I.x.plain} ; {I.y.plain})
        </p>
      </section>

      <p className="sr-only" aria-live="polite">
        AB = {d.plain}
      </p>
      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-3"
        data-testid="mp-dist"
      >
        <p className="text-ink-500 text-xs font-semibold uppercase tracking-wide">Longueur AB</p>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$AB = \\sqrt{(x_B - x_A)^2 + (y_B - y_A)^2}$`}
        </MathText>
        <MathText className="text-ink-900 block overflow-x-auto">
          {`$= \\sqrt{(${B.x} - ${par(A.x)})^2 + (${B.y} - ${par(A.y)})^2} = \\sqrt{${par(dx)}^2 + ${par(dy)}^2} = \\sqrt{${d2}}$`}
        </MathText>
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-lg font-bold">
            {Number.isInteger(d.value)
              ? `$AB = ${d.tex}$`
              : d.tex === `\\sqrt{${d2}}`
                ? `$AB = \\sqrt{${d2}} \\approx ${tx(formatNumber(d.value, 2))}$`
                : `$AB = \\sqrt{${d2}} = ${d.tex} \\approx ${tx(formatNumber(d.value, 2))}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={d.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: d.atoms })}
            />
          )}
        </div>
        {triangle && dx !== 0 && dy !== 0 && (
          <p className="text-ink-600 text-sm">
            Dans le triangle rectangle, AB est l’hypoténuse : AB² = {Math.abs(dx)}² + {Math.abs(dy)}
            ² = {d2} (Pythagore).
          </p>
        )}
      </section>
    </div>
  );
}
